import { FILE_TYPES } from '@shared/constants/fileTypes';
import React from 'react';
import DeDuplicatedMetricDocumentValidator from '../validators/DeDuplicatedMetricDocumentValidator';
import { showErrorToast } from '@shared/services/toastService';
import GET_DE_DUPLICATED_METRIC_DOCUMENT_FILE_URL from '../queries/getDeDuplicatedMetricDocumentFileUrl';
import { useLazyQuery } from '@apollo/client';
import MESSAGE_TARGETS from '../../../signalR/constants/signalRMessageTargets';
import { FILE_ERROR_MESSAGE_CODES } from '../../shared/constants/fileErrorMessageCodes';
import UploadButton from '@shared/components/buttons/UploadButton';
import { useNonBlockingFileUpload } from '@shared/hooks/useNonBlockingFileUpload';

const ACCEPTED_FILE_TYPES = [`.${FILE_TYPES.CSV}`, `.${FILE_TYPES.XLSX}`, `.${FILE_TYPES.XLS}`];

const IMPORTING_DOCUMENT_MESSAGE = 'Importing de-duplicated document...';
const IMPORT_DOCUMENT_SUCCESS_MESSAGE = 'File has been imported successfully.';
const IMPORT_DOCUMENT_ERROR_MESSAGE = 'Unable to import file.';

const ERROR_CODE_TO_MESSAGES_MAP = {
  [FILE_ERROR_MESSAGE_CODES.INVALID_FILE]: 'Please update the correct file template.',
  [FILE_ERROR_MESSAGE_CODES.WRONG_FILE_TEMPLATE]: 'Please update the correct file template.',
  [FILE_ERROR_MESSAGE_CODES.INVALID_DATA]: 'All entered fields must be 100 characters or less.'
};

const DeDuplicatedMetricImportButton = () => {
  const [getDocumentFileUrl] = useLazyQuery(GET_DE_DUPLICATED_METRIC_DOCUMENT_FILE_URL, {
    fetchPolicy: 'network-only'
  });

  const { uploadFile } = useNonBlockingFileUpload({
    loadingMessage: IMPORTING_DOCUMENT_MESSAGE,
    successMessage: IMPORT_DOCUMENT_SUCCESS_MESSAGE,
    errorMessage: IMPORT_DOCUMENT_ERROR_MESSAGE,
    signalRMessageTarget: MESSAGE_TARGETS.DE_DUPLICATED_CHECKPOINT_FILE_VALIDATION_NOTIFICATION,
    errorCodeToMessagesMap: ERROR_CODE_TO_MESSAGES_MAP
  });

  const handleFileChange = async e => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const { error } = DeDuplicatedMetricDocumentValidator.fileSchema.validate({
      type: file.type,
      size: file.size
    });

    if (error) {
      showErrorToast(error.message);
      return;
    }

    const fileUrlProvider = async () => {
      const { data: getDocumentFileUrlData, error: getDocumentFileUrlError } =
        await getDocumentFileUrl({
          variables: {
            fileName: file.name
          }
        });

      if (!getDocumentFileUrlData?.deDuplicatedMetricDocumentFileUrl || getDocumentFileUrlError) {
        throw new Error(IMPORT_DOCUMENT_ERROR_MESSAGE);
      }

      return getDocumentFileUrlData.deDuplicatedMetricDocumentFileUrl;
    };

    await uploadFile(file, fileUrlProvider);

    e.target.value = '';
  };

  return (
    <UploadButton
      className='primary-button import-button'
      label='De-Duplicated Import'
      accept={ACCEPTED_FILE_TYPES.join(',')}
      handleFileChange={handleFileChange}
      data-testid='de-duplicated-import-button-input'
    />
  );
};

export default DeDuplicatedMetricImportButton;
